// =============================================================================
// FILE: packages/renderer/src/components/upsell/WhatYoureMissing.tsx
// PURPOSE: A slim, route-aware "what else you're not getting" bar shown on EVERY
//   page — surfaces a premium capability the client isn't using yet and routes
//   them to explore it. Expands to a short list of top locked features.
// =============================================================================

import { useEffect, useMemo, useState, type JSX } from 'react';
import { Sparkles, ChevronRight, X, Lock, ArrowRight } from 'lucide-react';
import { useUsageStore } from '../../stores/usage.store.js';

const ACCENT = '#25B5A8';

interface MissThing { title: string; blurb: string; route: string; }

const MISSING: MissThing[] = [
  { title: 'Full 3D Revit project export', blurb: 'IFC model + Dynamo graph that sets up sheets, devices, parameters & view templates.', route: 'venue-studio' },
  { title: '43-venue generator library', blurb: 'Arenas, data centres, vaults, labs, heliports & more — one studio.', route: 'venue-studio' },
  { title: 'VR walkthroughs & cinematic renders', blurb: 'Immersive panoramas and render galleries for client buy-in.', route: 'venue-studio' },
  { title: 'R2 voice briefings', blurb: 'Bella, Roger & Prime narrate role-aware project status — offline.', route: 'site-audit' },
  { title: 'Mobile Site Audit auto-send', blurb: 'Begin→End audits auto-tagged and auto-sent to the office.', route: 'site-audit' },
  { title: 'Stacked loyalty discounts', blurb: 'RCDD + Unity + multi-year + returning — up to 60% off.', route: 'markup-checkout' },
  { title: 'Buy 2 systems, get 1 free', blurb: 'Bundle superior systems and unlock a free companion system.', route: 'markup-checkout' },
];

// Route → the single most relevant thing they're missing on that screen.
const BY_ROUTE: Record<string, number> = {
  dashboard: 1, 'venue-studio': 0, 'site-audit': 4, 'markup-checkout': 5,
  'rail-engine': 0, 'marine-cable': 0, 'frequency-lab': 2, pricing: 6, 'vending-machine': 0,
};

export function WhatYoureMissing({ route, onNavigate }: { route: string; onNavigate?: (r: string) => void }): JSX.Element | null {
  const usage = useUsageStore();
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  // Re-show on every route change so the client always sees what else they get.
  useEffect(() => { setDismissed(false); setOpen(false); }, [route]);

  const highlight = useMemo(() => MISSING[BY_ROUTE[route] ?? (Math.abs(hash(route)) % MISSING.length)] ?? MISSING[0]!, [route]);
  if (!highlight) return null;
  // Only show upgrade prompts AFTER the user has had their "aha moment"
  if (!usage.ahaMomentReached) return null;
  if (dismissed) return null;

  return (
    <div data-testid="what-youre-missing" className="relative">
      <div className="flex items-center gap-3 px-4 py-2 text-[12px]" style={{ background: `${ACCENT}12`, borderBottom: `1px solid ${ACCENT}2e` }}>
        <Lock className="h-3.5 w-3.5 shrink-0" style={{ color: ACCENT }} />
        <span className="text-zinc-400 shrink-0 hidden sm:inline">You're not getting:</span>
        <button data-testid="wym-highlight" onClick={() => onNavigate?.(highlight.route)} className="font-semibold truncate text-left hover:underline" style={{ color: ACCENT }} title={highlight.blurb}>
          {highlight.title}
        </button>
        <span className="text-zinc-500 truncate hidden md:inline">— {highlight.blurb}</span>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button data-testid="wym-see-all" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold" style={{ background: ACCENT, color: '#04121b' }}>
            See what you're missing <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`} />
          </button>
          <button data-testid="wym-dismiss" onClick={() => setDismissed(true)} className="text-zinc-500 hover:text-zinc-300"><X className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {open && (
        <div data-testid="wym-panel" className="absolute z-40 right-3 mt-1 w-[340px] max-w-[92vw] rounded-xl p-2 shadow-2xl" style={{ background: 'rgba(8,16,30,0.98)', border: `1px solid ${ACCENT}44` }}>
          <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-bold" style={{ color: ACCENT }}><Sparkles className="h-3.5 w-3.5" /> Unlock more with R2</div>
          <div className="space-y-1 max-h-[320px] overflow-y-auto">
            {MISSING.map((m, i) => (
              <button key={i} data-testid="wym-item" onClick={() => { onNavigate?.(m.route); setOpen(false); }} className="w-full text-left px-2.5 py-2 rounded-lg transition hover:brightness-125" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.14)' }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-semibold text-zinc-100">{m.title}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" style={{ color: ACCENT }} />
                </div>
                <div className="text-[10.5px] text-zinc-500">{m.blurb}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function hash(s: string): number { let h = 0; for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i); return h; }

export default WhatYoureMissing;
